import { IPatientRepository } from "../../adapters/driven/repositories/patientRepository";

export const PatientServiceImplFactory = ({
  patientRepository,
}: {
  patientRepository: IPatientRepository;
}) => {
  const getPatientList: IGetPatientList = async ({
    clinicId,
    floorIds,
    shouldCall = true,
  }) => {
    try {
      if (shouldCall) {
        const response = await patientRepository.getPatientListAPI({
          clinic_id: clinicId,
          floor_ids: floorIds,
          consent_status: [1, 2],
        });
        return {
          success: true,
          data: response,
        };
      }
      return {
        success: false,
        error: { message: "" },
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getPatientSummary: IGetPatientSummary = async ({
    clinicId,
    patientId,
  }) => {
    try {
      const response = await patientRepository.getPatientSummaryAPI({
        clinic_id: clinicId,
        patient_id: patientId,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getPatientCircadiaVital: IGetPatientCircadiaVital = async ({
    clinicId,
    patientId,
  }) => {
    try {
      const response = await patientRepository.getPatientCircadiaVitalAPI({
        clinic_id: clinicId,
        patient_id: patientId,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getPatientPCCVital: IGetPatientPCCVital = async ({
    clinicId,
    patientId,
  }) => {
    try {
      const response = await patientRepository.getPatientPCCVitalAPI({
        clinic_id: clinicId,
        patient_id: patientId,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getPatientEventList: IGetPatientEventList = async ({
    clinicId,
    patientId,
    eventType,
    eventDate,
    page,
    page_size,
    shouldCall = true,
  }) => {
    if (!shouldCall) {
      return {
        success: false,
        error: { message: "" },
      };
    }
    const payload: Parameters<
      typeof patientRepository.getPatientEventListAPI
    >[0] = {
      clinic_id: clinicId,
      resident_id: patientId,
      event_type: eventType,
      page,
      page_size,
    };
    if (eventDate) {
      payload.event_date = eventDate;
    }

    try {
      const response = await patientRepository.getPatientEventListAPI(payload);
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getPatientVitalGraph: IGetPatientVitalGraphService = async ({
    clinicId,
    endDate,
    patientId,
    typeOfRange,
    hourly_format = false,
  }) => {
    try {
      const response = await patientRepository.getPatientVitalGraphAPI({
        clinic_id: clinicId,
        resident_id: patientId,
        end_date: endDate,
        time_window: typeOfRange,
        hourly_format,
      });

      return {
        success: true,
        data: { data: response },
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getPatientRRGraph: IGetPatientRRGraphService = async ({
    clinicId,
    endDate,
    patientId,
    typeOfRange,
  }) => {
    try {
      const response = await patientRepository.getPatientRRGraphAPI({
        clinic_id: clinicId,
        patient_id: patientId,
        end_date: endDate,
        type_of_range: typeOfRange,
      });

      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getPatientNotes: IGetPatientNotesService = async (payload) => {
    try {
      const response = await patientRepository.getPatientNotesAPI(payload);
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getPatientNotificationSettings: IGetPatientNotificationSettingsService =
    async (payload) => {
      try {
        const response =
          await patientRepository.getPatientNotificationSettingsAPI(payload);
        return {
          success: true,
          data: response,
        };
      } catch (error) {
        return {
          success: false,
          error: { message: (error as unknown as Error).message },
        };
      }
    };

  const getDiagnoses: IGetDiagnosesService = async (payload) => {
    try {
      const response = await patientRepository.getDiagnosesAPI(payload);
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getPCCNotes: IGetPCCNotesService = async (payload) => {
    try {
      const response = await patientRepository.getPCCNotesAPI(payload);
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getPatientHmac: IGetPatientHmacService = async (payload) => {
    try {
      const response = await patientRepository.getPatientHmacAPI(payload);
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const addPatientNote: IAddPatientNoteService = async ({
    clinicId,
    patientId,
    file,
    notes,
  }) => {
    try {
      const response = await patientRepository.addPatientNoteAPI({
        clinic_id: clinicId,
        patient_id: patientId,
        file,
        notes,
        is_billable: false,
        type: "CircadiaNotes",
      });

      return {
        success: true,
        data: { data: response },
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getCircadiaIQ: IGetCircadiaIQ = async (payload) => {
    try {
      const response = await patientRepository.getCircadiaIQAPI(payload);
      return {
        success: true,
        data: { data: response },
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const generateCircadiaIQ: IGetGenerateCircadiaIQ = async (payload) => {
    try {
      const response = await patientRepository.generateCircadiaIQAPI({
        patient_id: payload.patient_id,
        clinic_id: payload.clinic_id,
        type: payload.type,
        date: payload.date,
      });
      return {
        success: true,
        data: { data: response },
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getPatientListDashboard: IGetPatientListDashboard = async (payload) => {
    try {
      const response = await patientRepository.getPatientListDashboardAPI({
        clinic_id: payload.clinic_id,
        floorIds: payload.floorIds,
        no_of_days: payload.no_of_days,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  return {
    getPatientList,
    getPatientEventList,
    getPatientSummary,
    getPatientCircadiaVital,
    getPatientPCCVital,
    getPatientVitalGraph,
    getPatientRRGraph,
    getPatientNotes,
    getPatientNotificationSettings,
    getDiagnoses,
    getPCCNotes,
    addPatientNote,
    getPatientHmac,
    getCircadiaIQ,
    generateCircadiaIQ,
    getPatientListDashboard,
  };
};
